import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { toast } from "sonner";
import { Package, ArrowRight, CheckCircle, ArrowCounterClockwise, ChatCircle, Trash } from "@phosphor-icons/react";

const PHASES = ["Quote", "Design", "Review", "Final Proof Sign-Off", "Production", "Shipping/Go Live", "Completed"];
const STATUS_TABS = [
  { id: "all", label: "All" },
  { id: "active", label: "Active" },
  { id: "closed", label: "Closed" },
];

export default function AdminDashboard() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");         // "all" | phase name
  const [statusFilter, setStatusFilter] = useState("active"); // active | closed | all

  const reload = () => api.get("/jobs").then((r) => setJobs(r.data));
  useEffect(() => { reload().finally(() => setLoading(false)); }, []);

  const closeFromList = async (j, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (j.phase === "Completed") return;
    const note = window.prompt(`Close "${j.project_name}"?\n\nOptional closing note (visible in the project activity log). Press Cancel to abort.`, "");
    if (note === null) return; // cancelled
    try {
      await api.post(`/jobs/${j.id}/complete`, { note });
      toast.success(`Project ${j.job_number} closed`);
      await reload();
    } catch (ex) {
      toast.error(ex.response?.data?.detail || "Failed to close project");
    }
  };

  const reopenFromList = async (j, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (j.phase !== "Completed") return;
    const note = window.prompt(`Reopen "${j.project_name}"?\n\nOptional reopen note. Press Cancel to abort.`, "");
    if (note === null) return;
    try {
      const { data } = await api.post(`/jobs/${j.id}/reopen`, { note });
      toast.success(`Reopened → ${data.phase}`);
      await reload();
    } catch (ex) {
      toast.error(ex.response?.data?.detail || "Failed to reopen");
    }
  };

  const deleteJob = async (j, e) => {
    e.preventDefault();
    e.stopPropagation();
    const confirm = window.prompt(`PERMANENTLY DELETE "${j.project_name}" (${j.job_number})?\n\nThis cannot be undone. Comments and job data will be removed forever.\n\nType DELETE to confirm.`, "");
    if (confirm !== "DELETE") return;
    try {
      await api.delete(`/admin/jobs/${j.id}`);
      toast.success(`Deleted ${j.job_number}`);
      await reload();
    } catch (ex) {
      toast.error(ex.response?.data?.detail || "Failed to delete project");
    }
  };

  const filtered = jobs.filter((j) => {
    if (statusFilter === "active" && j.phase === "Completed") return false;
    if (statusFilter === "closed" && j.phase !== "Completed") return false;
    if (filter !== "all" && j.phase !== filter) return false;
    return true;
  });
  const scoped = jobs.filter((j) =>
    statusFilter === "all" ? true
      : statusFilter === "active" ? j.phase !== "Completed"
      : j.phase === "Completed"
  );
  const byPhase = PHASES.reduce((acc, p) => { acc[p] = scoped.filter((j) => j.phase === p).length; return acc; }, {});

  return (
    <Layout>
      <div className="max-w-[1400px] mx-auto px-6 py-10">
        <div className="chip mb-3">// admin · pipeline</div>
        <h1 className="font-display text-5xl md:text-6xl mb-8">Pipeline<br/><span className="text-[var(--gc-orange)]">Control Room</span></h1>

        <div className="flex items-center gap-2 mb-6 flex-wrap" data-testid="status-tabs">
          {STATUS_TABS.map((t) => {
            const count = t.id === "all"
              ? jobs.length
              : t.id === "active"
                ? jobs.filter((j) => j.phase !== "Completed").length
                : jobs.filter((j) => j.phase === "Completed").length;
            return (
              <button
                key={t.id}
                onClick={() => { setStatusFilter(t.id); setFilter("all"); }}
                className={`px-4 py-2 text-sm font-semibold border-2 border-[var(--gc-navy)] transition-colors ${statusFilter === t.id ? 'bg-[var(--gc-navy)] text-white' : 'bg-white hover:bg-[var(--gc-bg-alt)]'}`}
                data-testid={`status-tab-${t.id}`}
              >
                {t.label} ({count})
              </button>
            );
          })}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-6">
          {PHASES.map((p) => (
            <button key={p} onClick={() => setFilter(p)}
              className={`p-4 border-2 text-left transition-all ${filter === p ? 'border-[var(--gc-orange)] bg-[var(--gc-orange-tint)]' : 'border-[var(--gc-navy)] bg-white hover:bg-[var(--gc-bg-alt)]'}`}
              data-testid={`filter-${p.replace(/[^a-z0-9]/gi, '-').toLowerCase()}`}>
              <div className="tech-label leading-tight">{p}</div>
              <div className="font-display text-4xl mt-1">{byPhase[p] || 0}</div>
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2 mb-4">
          <button onClick={() => setFilter("all")} className={`px-3 py-1.5 text-xs font-semibold border-2 border-[var(--gc-navy)] ${filter === "all" ? 'bg-[var(--gc-navy)] text-white' : 'bg-white hover:bg-[var(--gc-bg-alt)]'}`} data-testid="filter-all">All ({scoped.length})</button>
        </div>

        {loading ? (
          <div className="text-center py-20 tech-label">LOADING…</div>
        ) : filtered.length === 0 ? (
          <div className="sticker p-16 text-center">
            <Package size={40} className="mx-auto text-[var(--gc-orange)]" weight="bold" />
            <div className="font-display text-3xl mt-4">No jobs</div>
          </div>
        ) : (
          <div className="border-2 border-[var(--gc-navy)] overflow-hidden bg-white">
            <table className="w-full">
              <thead className="bg-[var(--gc-navy)] text-white">
                <tr className="text-left">
                  <th className="p-3 tech-label text-white">Job</th>
                  <th className="p-3 tech-label text-white">Customer</th>
                  <th className="p-3 tech-label text-white">Type</th>
                  <th className="p-3 tech-label text-white">Phase</th>
                  <th className="p-3 tech-label text-white">Updated</th>
                  <th className="p-3 tech-label text-white"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((j) => (
                  <tr key={j.id} className="border-b border-[var(--gc-border)] hover:bg-[var(--gc-bg-alt)]" data-testid={`admin-job-row-${j.id}`}>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <div>
                          <div className="font-mono-tech text-xs text-[var(--gc-text-muted)]">{j.job_number}</div>
                          <div className="font-semibold text-[var(--gc-navy)]">{j.project_name}</div>
                        </div>
                        {j.unread_message_count > 0 && (
                          <span
                            className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-[var(--gc-orange)] text-white shrink-0"
                            title={`${j.unread_message_count} unread message${j.unread_message_count === 1 ? "" : "s"}`}
                            data-testid={`unread-badge-${j.id}`}
                          >
                            <ChatCircle size={10} weight="fill"/> {j.unread_message_count}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="p-3">
                      <div className="text-sm">{j.customer?.first_name} {j.customer?.last_name}</div>
                      <div className="text-xs text-[var(--gc-text-muted)]">{j.customer?.business_name}</div>
                    </td>
                    <td className="p-3 text-sm">{j.project_type}</td>
                    <td className="p-3">
                      <span className="chip chip-orange">{j.phase}</span>
                    </td>
                    <td className="p-3 text-xs text-[var(--gc-text-muted)]">{new Date(j.updated_at).toLocaleDateString()}</td>
                    <td className="p-3">
                      <div className="flex items-center gap-2 flex-wrap justify-end">
                        {j.phase !== "Completed" ? (
                          <button
                            onClick={(e) => closeFromList(j, e)}
                            className="inline-flex items-center gap-1 text-xs px-2 py-1 border border-[var(--gc-success)] text-[var(--gc-success)] font-semibold hover:bg-green-50"
                            data-testid={`quick-close-${j.id}`}
                            title="Close this project"
                          >
                            <CheckCircle size={12} weight="bold"/> Close
                          </button>
                        ) : (
                          <button
                            onClick={(e) => reopenFromList(j, e)}
                            className="inline-flex items-center gap-1 text-xs px-2 py-1 border border-[var(--gc-navy)] text-[var(--gc-navy)] font-semibold hover:bg-[var(--gc-bg-alt)]"
                            data-testid={`quick-reopen-${j.id}`}
                            title="Reopen this project"
                          >
                            <ArrowCounterClockwise size={12} weight="bold"/> Reopen
                          </button>
                        )}
                        <button
                          onClick={(e) => deleteJob(j, e)}
                          className="inline-flex items-center gap-1 text-xs px-2 py-1 border border-[var(--gc-danger)] text-[var(--gc-danger)] font-semibold hover:bg-red-50"
                          data-testid={`delete-job-${j.id}`}
                          title="Permanently delete this project"
                        >
                          <Trash size={12} weight="bold"/> Delete
                        </button>
                        <Link to={`/admin/jobs/${j.id}`} className="inline-flex items-center gap-1 text-sm text-[var(--gc-orange)] font-semibold hover:underline" data-testid={`open-job-${j.id}`}>
                          Open <ArrowRight size={14} weight="bold"/>
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
}
